import { calendarDateOnly } from '@/utils/formatScheduleDate';
import { filterNmiOpenInstallmentRows } from '@/utils/nmiOpenInstallmentRows';

function shiftCalendarDate(ymd, days) {
  const [y, m, d] = calendarDateOnly(ymd).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

/** Last legal payment day as YYYY-MM-DD: the departure DATE minus N days
 *  (mirror of getPaymentDeadline in supabase/functions/_shared/nmi-schedule-quote.ts). */
function getPaymentDeadlineYmd(paymentSchedule = {}, departureDate) {
  const departureRaw = paymentSchedule.departure_date ?? departureDate;
  if (!departureRaw) return null;
  const lastDays = Number(paymentSchedule.last_payment_days_before_departure ?? 5);
  const ymd = String(departureRaw).match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (ymd) return shiftCalendarDate(ymd, -lastDays);
  const dep = new Date(String(departureRaw));
  if (Number.isNaN(dep.getTime())) return null;
  return shiftCalendarDate(dep.toISOString().slice(0, 10), -lastDays);
}

/** Dates from the anchor (the next upcoming due date) every 7/14 days, never
 *  past the deadline, splitting the balance evenly (remainder on the last). */
function computeScheduleDates(remainingCents, deadlineYmd, frequency, anchorYmd) {
  const intervalDays = frequency === 'weekly' ? 7 : 14;
  const dueDates = [];
  for (let date = anchorYmd; date <= deadlineYmd && dueDates.length < 99; date = shiftCalendarDate(date, intervalDays)) {
    dueDates.push(date);
  }
  const count = dueDates.length;
  const base = count > 0 ? Math.floor(remainingCents / count) : 0;
  const remainder = remainingCents - base * count;
  const dates = dueDates.map((date, i) => ({ date, amount_cents: i === count - 1 ? base + remainder : base }));
  return { dates, per_payment_cents: base, num_payments: dates.length };
}

/**
 * Client-side Change Schedule preview for NMI trips — mirrors the edge quote so
 * the modal works before/without a redeployed preview-schedule function.
 *
 * KEEP IN LOCKSTEP with buildNmiScheduleChangeQuote (nmi-schedule-quote.ts):
 * the new plan starts ON the next upcoming due date, only `pending` payments
 * are re-spread (a retrying/declined one keeps its date and amount), and a
 * change to the current frequency is no change at all (null).
 */
export function buildNmiScheduleChangePreview({
  upcomingRows = [],
  paymentPlan = 'weekly',
  proposedFrequency,
  paymentSchedule = {},
  totalAmountCents = 0,
}) {
  if (proposedFrequency === paymentPlan) return null;
  const openRows = filterNmiOpenInstallmentRows(upcomingRows)
    .map((r) => ({
      date: calendarDateOnly(r.date),
      amount_cents: Number(r.amount_cents) || 0,
      pending: (r._nmi_status ?? 'pending') === 'pending',
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (openRows.length === 0) return null;
  const movable = openRows.filter((r) => r.pending);
  const held = openRows.filter((r) => !r.pending);
  if (movable.length === 0) return null;

  const remainingCents = openRows.reduce((s, r) => s + r.amount_cents, 0);
  const movableCents = movable.reduce((s, r) => s + r.amount_cents, 0);
  const total = totalAmountCents > 0 ? totalAmountCents : remainingCents;
  const deadlineYmd = getPaymentDeadlineYmd(paymentSchedule);
  if (!deadlineYmd) return null;

  const strip = ({ date, amount_cents }) => ({ date, amount_cents });
  const currentSchedule = {
    dates: openRows.map(strip),
    num_payments: openRows.length,
    per_payment_cents: openRows[0]?.amount_cents ?? 0,
  };

  const respread = computeScheduleDates(movableCents, deadlineYmd, proposedFrequency, movable[0].date);
  if (respread.dates.length === 0) return null;
  const proposedDates = [...held.map(strip), ...respread.dates].sort((a, b) => a.date.localeCompare(b.date));

  return {
    paid_amount_cents: Math.max(0, total - remainingCents),
    remaining_cents: remainingCents,
    current_frequency: paymentPlan,
    proposed_frequency: proposedFrequency,
    current_schedule: currentSchedule,
    proposed_schedule: {
      dates: proposedDates,
      per_payment_cents: respread.per_payment_cents,
      num_payments: proposedDates.length,
    },
  };
}
